
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { sanitizeInput } from "@/utils/security";
import { devLog, devError } from "@/utils/secureLogger";
import { getSafeRedirectPath, getRedirectFromUrl } from "@/utils/safeRedirect";
import { secureStorage } from "@/utils/secureStorage";

interface UseAuthSubmissionProps {
  isSignUp: boolean;
  setIsSignUp: (value: boolean) => void;
  setIsVerifying: (value: boolean) => void;
  setPendingVerificationEmail: (email: string) => void;
  setPendingCredentials: (creds: {email: string, password: string, username: string} | null) => void;
  setSignupDisabled: (value: boolean) => void;
}

export const useAuthSubmission = ({
  isSignUp,
  setIsSignUp,
  setIsVerifying,
  setPendingVerificationEmail,
  setPendingCredentials,
  setSignupDisabled
}: UseAuthSubmissionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signUp, resendVerificationEmail } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Safely get redirect path from either router state or URL params
  // Use safe redirect validation to prevent open redirect attacks
  const stateFrom = (location.state as any)?.from?.pathname;
  const urlParams = new URLSearchParams(location.search);
  const urlRedirect = urlParams.get('redirect');
  
  // Prioritize router state, fall back to URL param, then default to homepage
  const rawFrom = stateFrom || urlRedirect || "/";
  const from = getSafeRedirectPath(rawFrom, "/");

  const handleSubmit = async (email: string, password: string, username: string, captchaToken?: string) => {
    setIsLoading(true);
    
    try {
      // Sanitize inputs
      const sanitizedEmail = sanitizeInput(email);
      const sanitizedUsername = sanitizeInput(username);
      
      if (isSignUp) {
        if (!sanitizedUsername) {
          toast({
            title: "Error",
            description: "Username is required",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
        
        devLog("Starting signup process");
        const { error, isSignupDisabled, isExistingUser, isPasswordWeak, pendingCredentials: credentials } = await signUp(sanitizedEmail, password, sanitizedUsername, captchaToken);
        
        if (isSignupDisabled) {
          setSignupDisabled(true);
          setIsLoading(false);
          return;
        }
        
        if (isPasswordWeak) {
          // Error message already handled by useSignUp
          setIsLoading(false);
          return;
        }
        
        // Handle case where user already exists
        if (isExistingUser || (error && error.message?.includes("User already registered"))) {
          toast({
            title: "Account Already Exists",
            description: "This email is already registered. Please sign in instead.",
            variant: "warning",
          });
          // Automatically switch to sign in mode
          setIsSignUp(false);
          setIsLoading(false);
          return;
        }
        
        if (error) throw error;
        
        devLog("Signup process initiated, storing email for verification");
        secureStorage.set('pendingVerificationEmail', sanitizedEmail, 3600); // 1 hour expiry
        
        // Store credentials for account creation after verification
        // SECURITY FIX: Never store password in localStorage - user must re-enter after verification
        if (credentials) {
          setPendingCredentials(credentials);
          // Only store email and username - password is intentionally excluded for security
          secureStorage.set('pendingCredentials', {email: sanitizedEmail, password: '', username: sanitizedUsername}, 3600);
        }
        
        setPendingVerificationEmail(sanitizedEmail);
        setIsVerifying(true);
        
        toast({
          title: "Check Your Email",
          description: "We've sent you a verification link. Please check your inbox to verify your email.",
        });
      } else {
        const result = await signIn(sanitizedEmail, password, captchaToken);
        
        if (!result.success) {
          const errMsg = result.message || result.error?.message || "";
          const errCode = (result.error as any)?.code || "";
          const isUnconfirmed =
            errCode === "email_not_confirmed" ||
            /email not confirmed/i.test(errMsg);

          if (isUnconfirmed) {
            secureStorage.set('pendingVerificationEmail', sanitizedEmail, 3600);
            setPendingVerificationEmail(sanitizedEmail);
            setPendingCredentials({ email: sanitizedEmail, password: '', username: '' });
            setIsVerifying(true);

            try {
              await resendVerificationEmail(sanitizedEmail);
            } catch (e) {
              devError("Failed to resend verification email:", e);
            }

            toast({
              title: "Email not confirmed",
              description: "We've sent you a new verification code. Please check your inbox.",
            });
            setIsLoading(false);
            return;
          }

          toast({
            title: "Authentication error",
            description: errMsg || "Invalid login credentials",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
        
        
        // Only show success toast for successful login
        toast({
          title: "Success",
          description: "Logged in successfully",
          variant: "success",
        });
        
        navigate(from, { replace: true });
      }
    } catch (error: any) {
      devError("Authentication error:", error);
      toast({
        title: "Authentication error",
        description: error.message || "An error occurred during authentication",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleSubmit,
    from
  };
};
